import {
  Avatar,
  Box,
  ButtonBase,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LockOutlined, WarningAmberOutlined } from "@mui/icons-material";
import {
  selectAccount,
  selectAccountDisplayName,
  selectEmail,
  selectProfileImage,
  selectUserName,
} from "redux/authSlice";
import { useAppSelector } from "redux/hooks";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import LogoutConfirmDialog from "global/components/LogoutConfirmDialog/LogoutConfirmDialog";
import { usePopover } from "hooks/use-popover";
import { useLogout } from "hooks/use-logout";
import { useFeatures } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import { hasAccessTo, isAdmin } from "layouts/Config";
import strings from "global/constants/StringConstants";
import DropdownDownIcon from "assets/icons/DropdownDownIcon.svg";
import { panel, profileMenu } from "theme/colors";
import { SignOutNavIcon } from "utils/TopNavIcons";
import WorkspaceSwitcher from "layouts/components/WorkspaceSwitcher";

const TopNavSection = () => {
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const userEmail = useAppSelector(selectEmail);
  const userName = useAppSelector(selectUserName);
  const popover = usePopover<any>();
  const organizationLabelId = "account-menu-organization-label";
  const profileImage = useAppSelector(selectProfileImage);
  const accountName = useAppSelector(selectAccount);
  const accountDisplayName = useAppSelector(selectAccountDisplayName);
  // Prefer the name the account chose for itself; `accountName` is the tenant
  // key, which is all older logins and never-named accounts have to show.
  const accountLabel = accountDisplayName || accountName;
  const {
    isConfirmOpen,
    isLoggingOut,
    requestLogout,
    confirmLogout,
    cancelLogout,
  } = useLogout();

  // Mirrors the Personal / Organization split in the Settings side menu, so the
  // dropdown offers the same destinations as the page it links into. Admins get
  // the whole menu; every other login gets Personal only — the Organization
  // group is dropped wholesale.
  const userIsAdmin = isAdmin();
  const { state: featureState } = useFeatures();

  const personalItems = [
    {
      href: viewpaths.myProfileViewPath,
      label: "My Profile",
      condition: true,
    },
    {
      href: `${viewpaths.securityViewPath}?tab=mfa`,
      label: "Security",
      condition: true,
    },
    {
      href: viewpaths.delegationViewPath,
      label: "Delegation",
      condition: userIsAdmin,
      // The destination page fully blocks itself when this is gated (see
      // `Delegation.tsx`'s `FeatureBanner`) — the row still navigates there,
      // this just tells the admin why before they click.
      featureKey: FeatureKeys.ENVELOPE_DELEGATION,
    },
    {
      href: viewpaths.ticketsViewPath,
      label: "Support Tickets",
      condition: true,
    },
  ];

  const organizationItems = [
    {
      href: `${viewpaths.viewMyPlanViewPath}?tab=account-usage`,
      label: "My Plan",
      condition: hasAccessTo(
        strings.resources.billing,
        strings.fetchPermission,
      ),
    },
    {
      href: viewpaths.rolesViewPath,
      label: "Roles",
      condition: userIsAdmin,
    },
    {
      href: viewpaths.teamViewPath,
      label: "Teams",
      condition: userIsAdmin,
    },
    {
      href: viewpaths.workspacesViewPath,
      label: "Workspaces",
      condition: userIsAdmin,
      featureKey: FeatureKeys.WORKSPACES,
    },
    {
      href: viewpaths.documentSettingsViewPath,
      label: "Document Settings",
      condition: userIsAdmin,
    },
    {
      href: viewpaths.brandingViewPath,
      label: "Branding",
      condition: userIsAdmin,
      featureKey: FeatureKeys.BRANDING_CUSTOM,
    },
    {
      href: viewpaths.securityPoliciesViewPath,
      label: "Security Policies",
      condition: userIsAdmin,
    },
    {
      href: viewpaths.enterpriseSsoViewPath,
      label: "Enterprise SSO",
      condition: userIsAdmin,
    },
    {
      href: viewpaths.apiKeysViewPath,
      label: "Integrations",
      condition: true,
      featureKey: FeatureKeys.API_ACCESS,
    },
  ];

  // Non-admins never see the Organization group, even where an individual item
  // would otherwise pass its own permission check.
  const visibleOrganizationItems = userIsAdmin
    ? organizationItems.filter((item) => item.condition)
    : [];

  // Text-only rows, as in the reference — no leading icons.
  const menuRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    padding: "10px 12px",
    borderRadius: "9px",
    fontSize: "13.5px",
    fontWeight: 600,
    fontFamily: (theme: Theme) => theme.typography.fontFamily,
    lineHeight: 1.4,
    color: profileMenu.itemText,
    textAlign: "left" as const,
    textDecoration: "none",
    "&:hover": {
      backgroundColor: profileMenu.itemHoverBg,
      color: profileMenu.itemHoverText,
    },
    "&.Mui-focusVisible": {
      outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
      outlineOffset: "-2px",
    },
  };

  const renderMenuItem = (item: {
    href: string;
    label: string;
    featureKey?: string;
  }) => {
    const gate = item.featureKey ? featureState(item.featureKey) : undefined;
    const isGated = !!gate && gate.status !== strings.FEATURE_STATUS_AVAILABLE;
    const gateTooltip = isGated
      ? gate!.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : ((gate as any).disabledMessage ??
          strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : "";

    const row = (
      <ButtonBase
        key={item.label}
        href={item.href}
        onClick={popover.handleClose}
        sx={{
          ...menuRowSx,
          ...(isGated && { color: profileMenu.sectionLabel }),
        }}
        LinkComponent={RouterLink || "div"}
      >
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {item.label}
        </Box>
        {isGated &&
          (gate!.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? (
            <WarningAmberOutlined sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }} />
          ) : (
            <LockOutlined sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }} />
          ))}
      </ButtonBase>
    );

    if (!isGated) return row;

    return (
      <Tooltip key={item.label} title={gateTooltip} placement="left" arrow>
        <Box component="span" sx={{ display: "block" }}>
          {row}
        </Box>
      </Tooltip>
    );
  };

  const getMenuSection = () => {
    return (
      <Stack direction="column" spacing={0}>
        {/* Identity header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing="12px"
          sx={{
            padding: "16px 18px",
            borderBottom: `1px solid ${profileMenu.divider}`,
          }}
        >
          <Avatar
            src={profileImage}
            alt={
              userName ? `${userName} profile picture` : "User profile picture"
            }
            sx={{ width: 42, height: 42, flexShrink: 0 }}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "13.5px",
                  fontWeight: 800,
                  color: panel.title,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {userName}
              </Typography>
              {userIsAdmin && (
                <Box
                  sx={{
                    flexShrink: 0,
                    fontSize: "9.5px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: profileMenu.adminChipText,
                    backgroundColor: profileMenu.adminChipBg,
                    borderRadius: "999px",
                    padding: "3px 8px",
                  }}
                >
                  ADMIN
                </Box>
              )}
            </Box>
            <Typography
              title={userEmail}
              sx={{
                fontSize: "11.5px",
                color: profileMenu.sectionLabel,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {userEmail}
            </Typography>
            {accountLabel && (
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: profileMenu.orgName,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {accountLabel}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Personal */}
        <Box sx={{ padding: "8px" }}>
          {personalItems.map((item) => item.condition && renderMenuItem(item))}
        </Box>

        {/* Organization — admins only */}
        {visibleOrganizationItems.length > 0 && (
          <Box
            role="group"
            aria-labelledby={organizationLabelId}
            sx={{
              borderTop: `1px solid ${profileMenu.divider}`,
              padding: "8px",
            }}
          >
            <Typography
              id={organizationLabelId}
              sx={{
                padding: "8px 12px 4px",
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: profileMenu.sectionLabel,
              }}
            >
              ORGANIZATION
            </Typography>
            {visibleOrganizationItems.map((item) => renderMenuItem(item))}
          </Box>
        )}

        {/* Sign out */}
        <Box
          sx={{
            borderTop: `1px solid ${profileMenu.divider}`,
            padding: "8px",
          }}
        >
          <ButtonBase
            onClick={() => {
              popover.handleClose();
              requestLogout();
            }}
            sx={{
              ...menuRowSx,
              gap: "10px",
              fontWeight: 700,
              color: profileMenu.signOut,
              "&:hover": {
                backgroundColor: profileMenu.signOutHoverBg,
                color: profileMenu.signOut,
              },
            }}
          >
            <SignOutNavIcon sx={{ fontSize: 16 }} />
            Sign out
          </ButtonBase>
        </Box>
      </Stack>
    );
  };

  return (
    <Stack direction="row" alignItems="center">
      <WorkspaceSwitcher />
      <Box
        component="button"
        type="button"
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-label={userName ? `Account menu, ${userName}` : "Account menu"}
        sx={{
          display: "flex",
          alignItems: "center",
          border: "none",
          background: "transparent",
          p: 0,
          cursor: "pointer",
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "2px",
            borderRadius: "8px",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            borderWidth: 2,
            borderStyle: "solid",
            borderColor: "divider",
            height: 40,
            width: 40,
            borderRadius: "50%",
          }}
        >
          <Avatar
            component="span"
            sx={{
              height: 32,
              width: 32,
            }}
            src={profileImage}
            alt=""
          />
        </Box>
        {/*
          The phone references draw a bare avatar with no caret, and those 24px
          are what a screen name like "Sign Requests" needs to fit the app bar
          at 375 instead of truncating. The popover anchors off the button
          itself, so dropping the glyph does not move the menu.
        */}
        <Box
          component="img"
          src={DropdownDownIcon}
          alt=""
          aria-hidden="true"
          sx={{ display: { xs: "none", sm: "block" } }}
        />
      </Box>
      <CustomPopper
        open={popover.open}
        anchorRef={popover.anchorRef.current}
        handleClose={popover.handleClose}
        children={getMenuSection()}
        ariaLabel="Account menu"
        width={lgUp ? "280px" : "auto"}
        padding={"0px !important"}
        borderRadius={profileMenu.radius}
        border={`1px solid ${profileMenu.border}`}
        shadow={panel.shadow}
      />
      <LogoutConfirmDialog
        isOpen={isConfirmOpen}
        isLoggingOut={isLoggingOut}
        onCancel={cancelLogout}
        onConfirm={confirmLogout}
      />
    </Stack>
  );
};

export default TopNavSection;
